/**
 * Direct-to-bucket uploads (Cloudflare R2 / S3) using signed PUT links from the API.
 *
 * The PUT goes straight to the bucket with XMLHttpRequest (for progress events), never through
 * axiosInstance: its Authorization header would break the signature and the bucket CORS check.
 */
import { prepareAttachmentFile } from "./prepareAttachmentFile";

const EXT_TYPES = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
  gif: "image/gif",
  mp4: "video/mp4",
  mov: "video/quicktime",
  webm: "video/webm",
  pdf: "application/pdf",
};

export const MEDIA_ACCEPT = Object.keys(EXT_TYPES)
  .map((ext) => `.${ext}`)
  .join(",");

export function fileContentType(file) {
  if (file?.type) return file.type;
  const ext = String(file?.name || "").split(".").pop().toLowerCase();
  return EXT_TYPES[ext] || "application/octet-stream";
}

export function mediaKindOf(contentType) {
  const t = String(contentType || "");
  if (t.startsWith("image/")) return "image";
  if (t.startsWith("video/")) return "video";
  if (t === "application/pdf") return "pdf";
  return null;
}

export function formatBytes(bytes) {
  const n = Number(bytes) || 0;
  if (n >= 1024 * 1024 * 1024) return `${(n / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  if (n >= 1024) return `${Math.round(n / 1024)} KB`;
  return `${n} B`;
}

/** Natural width/height of an image or video file; nulls when the browser cannot read it. */
export function readMediaDimensions(file) {
  const kind = mediaKindOf(fileContentType(file));
  if (kind !== "image" && kind !== "video") return Promise.resolve({ width: null, height: null });
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const done = (width, height) => {
      URL.revokeObjectURL(url);
      resolve({ width: width || null, height: height || null });
    };
    if (kind === "image") {
      const img = new Image();
      img.onload = () => done(img.naturalWidth, img.naturalHeight);
      img.onerror = () => done(null, null);
      img.src = url;
    } else {
      const video = document.createElement("video");
      video.preload = "metadata";
      video.onloadedmetadata = () => done(video.videoWidth, video.videoHeight);
      video.onerror = () => done(null, null);
      video.src = url;
    }
  });
}

function abortError() {
  const err = new Error("Upload cancelled");
  err.name = "AbortError";
  return err;
}

/** PUT a file to a signed URL. Resolves on 2xx; rejects with an Error (name "AbortError" on cancel). */
export function putToSignedUrl({ url, method = "PUT", headers = {}, file, onProgress, signal }) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortError());
      return;
    }
    const xhr = new XMLHttpRequest();
    xhr.open(method, url);
    Object.entries(headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Storage rejected the upload (HTTP ${xhr.status}).`));
    };
    xhr.onerror = () => reject(new Error("Network error while uploading. Check your connection and try again."));
    xhr.onabort = () => reject(abortError());
    signal?.addEventListener("abort", () => xhr.abort(), { once: true });
    xhr.send(file);
  });
}

/**
 * Upload one file into a DM project's media library.
 * Steps: ask the API for a signed link, PUT the file to the bucket, then confirm with the API.
 * `compress` re-encodes images to WebP (max 2560px) first; posters keep original quality by default.
 * Returns the ready media row.
 */
export async function uploadDmMedia(api, projectId, file, { contentId, onProgress, signal, compress = false } = {}) {
  const prepared = compress ? await prepareAttachmentFile(file, { maxEdge: 2560, quality: 0.9 }) : file;
  const contentType = fileContentType(prepared);
  const [{ width, height }, reserved] = await Promise.all([
    readMediaDimensions(prepared),
    api.getUploadUrl(projectId, {
      filename: prepared.name,
      content_type: contentType,
      size: prepared.size,
      ...(contentId ? { content_id: contentId } : {}),
    }),
  ]);
  const { media, upload } = reserved;
  try {
    await putToSignedUrl({ ...upload, file: prepared, onProgress, signal });
  } catch (err) {
    api.deleteMedia(media.Media_Id).catch(() => {});
    throw err;
  }
  onProgress?.(1);
  return api.completeMedia(media.Media_Id, { width, height });
}

const SETUP_EXT_TYPES = {
  ...EXT_TYPES,
  svg: "image/svg+xml",
  json: "application/json",
  plist: "application/x-plist",
  xml: "application/xml",
  txt: "text/plain",
  csv: "text/csv",
  zip: "application/zip",
};

export const SETUP_FILE_ACCEPT = Object.keys(SETUP_EXT_TYPES)
  .filter((ext) => !["mp4", "mov", "webm"].includes(ext))
  .map((ext) => `.${ext}`)
  .join(",");

/** Upload one file to a DM setup step (private bucket). Returns the ready file entry. */
export async function uploadSetupFile(api, tenantStepId, file, { field, onProgress, signal } = {}) {
  const ext = String(file?.name || "").split(".").pop().toLowerCase();
  const contentType = file?.type || SETUP_EXT_TYPES[ext] || "application/octet-stream";
  const { file: entry, upload } = await api.setupUploadUrl(tenantStepId, {
    filename: file.name,
    content_type: contentType,
    size: file.size,
    ...(field ? { field } : {}),
  });
  try {
    await putToSignedUrl({ ...upload, file, onProgress, signal });
  } catch (err) {
    api.deleteSetupFile(tenantStepId, entry.id).catch(() => {});
    throw err;
  }
  onProgress?.(1);
  return api.completeSetupFile(tenantStepId, { file_id: entry.id });
}
